import React from 'react';
import PropTypes from 'prop-types';

import {Dialog, DialogActions, DialogContent, DialogTitle} from 'views/components';
import {Button, Chip, Grid2 as Grid, MenuItem, Stack, TextField, Typography} from 'views/components';
import {Translate, translate} from 'views/module';

import {convertToE164} from 'utils/miscellaneous/e164';
import {RELATIONSHIP_ROLES, RELATIONSHIP_TYPES} from 'utils/backend/loom/family/family_adapter';

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  relationship: '',
  priority: '',
  roles: ['CARE_COORDINATOR'],
};

const toForm = (member) => ({
  firstName: member.firstName,
  lastName: member.lastName,
  email: member.email ?? '',
  phoneNumber: member.phoneNumber ?? '',
  relationship: member.relationship,
  priority: member.priority ?? '',
  roles: member.roles,
});

const FamilyMemberDialog = (props) => {
  const {member, onClose, onSave} = props;

  const [form, setForm] = React.useState(member ? toForm(member) : EMPTY_FORM);
  const [saving, setSaving] = React.useState(false);

  const phoneInput = form.phoneNumber.trim();
  const phone = phoneInput ? convertToE164(phoneInput, ['CA', 'US']) : null;
  const phoneInvalid = Boolean(phone && !phone.isValid);
  const emailInput = form.email.trim();
  const emailInvalid = Boolean(emailInput) && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput);
  const priorityInvalid = form.priority !== '' && Number(form.priority) < 1;
  const canSave = form.firstName.trim() && form.lastName.trim() && form.relationship &&
    !emailInvalid && !phoneInvalid && !priorityInvalid && !saving;

  const setField = (key) => (value) => setForm((current) => ({...current, [key]: value}));

  const setPriority = (value) => {
    if (/^\d{0,3}$/.test(value)) setForm((current) => ({...current, priority: value}));
  };

  const toggleRole = (role) => {
    setForm((current) => ({
      ...current,
      roles: current.roles.includes(role) ?
        current.roles.filter((selected) => selected !== role) :
        [...current.roles, role],
    }));
  };

  const submit = async () => {
    setSaving(true);
    await onSave({
      ...form,
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: emailInput,
      phoneNumber: phone ? phone.e164Format : '',
    });
    setSaving(false);
  };

  return (
    <Dialog onClose={onClose}>
      <DialogTitle onClose={onClose}>
        <Translate expression={member ? 'family.FamilyMembers.EditTitle' : 'family.FamilyMembers.AddTitle'} />
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{pt: 1}}>
          <Grid size={{xs: 12, sm: 6}}>
            <TextField
              fullWidth
              required
              label={translate('family.FamilyMembers.Fields.FirstName')}
              value={form.firstName}
              onChange={setField('firstName')}
            />
          </Grid>
          <Grid size={{xs: 12, sm: 6}}>
            <TextField
              fullWidth
              required
              label={translate('family.FamilyMembers.Fields.LastName')}
              value={form.lastName}
              onChange={setField('lastName')}
            />
          </Grid>
          <Grid size={{xs: 12, sm: 8}}>
            <TextField
              select
              fullWidth
              required
              label={translate('family.FamilyMembers.Fields.Relationship')}
              value={form.relationship}
              onChange={setField('relationship')}
            >
              {
                RELATIONSHIP_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    <Translate expression={`family.FamilyMembers.Relationship.${type}`} />
                  </MenuItem>
                ))
              }
            </TextField>
          </Grid>
          <Grid size={{xs: 12, sm: 4}}>
            <TextField
              fullWidth
              label={translate('family.FamilyMembers.Fields.Priority')}
              value={form.priority}
              onChange={setPriority}
              slotProps={{htmlInput: {inputMode: 'numeric'}}}
            />
          </Grid>
          <Grid size={{xs: 12, sm: 6}}>
            <TextField
              fullWidth
              type='email'
              label={translate('family.FamilyMembers.Fields.Email')}
              value={form.email}
              onChange={setField('email')}
              helperText={emailInvalid ? translate('family.FamilyMembers.InvalidEmail') : ' '}
              slotProps={{formHelperText: {sx: {color: emailInvalid ? 'error.main' : 'text.secondary'}}}}
            />
          </Grid>
          <Grid size={{xs: 12, sm: 6}}>
            <TextField
              fullWidth
              label={translate('family.FamilyMembers.Fields.Phone')}
              value={form.phoneNumber}
              onChange={setField('phoneNumber')}
              helperText={phoneInvalid ? translate('family.FamilyMembers.InvalidPhone') : ' '}
              slotProps={{formHelperText: {sx: {color: phoneInvalid ? 'error.main' : 'text.secondary'}}}}
            />
          </Grid>
          <Grid size={12}>
            <Stack spacing={1}>
              <Typography variant='subtitle2'>
                <Translate expression='family.FamilyMembers.Fields.Roles' />
              </Typography>
              <Stack direction='row' sx={{flexWrap: 'wrap', gap: 1}}>
                {
                  RELATIONSHIP_ROLES.map((role) => (
                    <Chip
                      key={role}
                      label={translate(`family.FamilyMembers.Role.${role}`)}
                      color={form.roles.includes(role) ? 'primary' : 'default'}
                      variant={form.roles.includes(role) ? 'filled' : 'outlined'}
                      onClick={() => toggleRole(role)}
                    />
                  ))
                }
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={onClose}>
          <Translate expression='family.FamilyMembers.Cancel' />
        </Button>
        <Button disabled={!canSave} onClick={submit}>
          <Translate expression='family.FamilyMembers.Save' />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

FamilyMemberDialog.propTypes = {
  member: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default React.memo(FamilyMemberDialog);
